import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { UrlEnum } from '../../enum/url.enum';
import { ChangePasswordModel } from '../../model/auth/change-password.model';
import { DemoLoginBodyModel } from '../../model/auth/demo-login.model';
import { ForgetPasswordModel } from '../../model/auth/forget-password.model';
import { LoginBodyModel } from '../../model/auth/login.model';
import { OtpResendModel } from '../../model/auth/otp-resend.model';
import { OtpValidationModel } from '../../model/auth/otp-validation.model';
import { ResetPasswordModel } from '../../model/auth/reset-password.model';
import {
  CorporateSignupBodyModel,
  SignupBodyModel,
} from '../../model/auth/signup.model';
import { HttpRequestService } from '../http-request/http-request.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UtilitiesService } from '../utilities/utilities.service';
import { AssignCorporateRoleModel } from '../../model/corporate/assaign-corporate-role.model';
import { CreateCustomRoleModel } from '../../model/corporate/create-custom-role.model';
import { addCorporateFeatureModel } from '../../model/corporate/add-corporate-feature.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public shouldShowToast = false;
  private loginApi = UrlEnum.SHOHAY_AUTH;

  private isAuthenticated = new BehaviorSubject<boolean>(
    !!this.localStorageService.getToken()
  );
  authState$ = this.isAuthenticated.asObservable();

  constructor(
    private readonly utilitiesService: UtilitiesService,
    private readonly httpRequestService: HttpRequestService,
    private readonly localStorageService: LocalStorageService
  ) {
    this.isAuthenticated.next(!!this.localStorageService.getToken());
  }

  public isLoggedIn(): boolean {
    return !!(
      this.localStorageService.getToken() &&
      this.localStorageService.getSessionUser()
    );
  }

  public login(body: LoginBodyModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/account/login`, body, { skipAuth: true })
      .pipe(
        tap((response: any) => {
          if (response && response.token) {
            this.localStorageService.setToken(response.token);
            this.localStorageService.setSessionUser(response.user);
            this.isAuthenticated.next(true);
          }
        })
      );
  }

  public demoLogin(body: DemoLoginBodyModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/users/demo-sign-up`, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public logout() {
    this.localStorageService.clearSessionUser();
    this.isAuthenticated.next(false); // Emitting false to all subscribers
  }

  public signup(
    body: SignupBodyModel,
    isCorporate: boolean = false
  ): Observable<any> {
    let apiUrl = `${this.loginApi}/users`;
    if (isCorporate) {
      apiUrl = `${apiUrl}/corporate-sign-up`;
    }
    return this.httpRequestService
      .post(apiUrl, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public corporateSignup(body: CorporateSignupBodyModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/users/corporate-sign-up`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public changePassword(body: ChangePasswordModel): Observable<any> {
    return this.httpRequestService
      .patch(`${this.loginApi}/account/reset-password`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public resendOtp(body: OtpResendModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/otp/resend`, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public validateOtp(body: OtpValidationModel): Observable<any> {
    return this.httpRequestService
      .patch(`${this.loginApi}/otp/validate`, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public sendMail(body: ForgetPasswordModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/reset-password/request`, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public resetPassword(body: ResetPasswordModel): Observable<any> {
    return this.httpRequestService
      .patch(`${this.loginApi}/reset-password`, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public deActiveEmployee(userId: String): Observable<any> {
    return this.httpRequestService
      .patch(`${this.loginApi}/users/de-active-employee/${userId}`, {})
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getRoleByCompany(companyId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.loginApi}/roles/get-corporate-role/${companyId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public assignRoleToEmployee(
    userId: string,
    body: AssignCorporateRoleModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.loginApi}/users/update-corporate-role/${userId}`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public addPermission(body: addCorporateFeatureModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/feature-management/feature-role-create`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public removeRoleToEmployee(userId: string, roleId: string): Observable<any> {
    return this.httpRequestService
      .patch(
        `${this.loginApi}/users/remove-corporate-role/${userId}/${roleId}`,
        {}
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getHistoryByCompany(companyId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.loginApi}/roles/get-corporate-history/${companyId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getHistoryByRole(companyId: string, roleId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.loginApi}/roles/get-history/${companyId}/${roleId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getCorporateFeature() {
    return this.httpRequestService.get(
      `${this.loginApi}/feature-management/get-corporate-feature`
    );
  }

  public removePermission(featureId: string, roleId: string) {
    return this.httpRequestService
      .delete(
        `${this.loginApi}/feature-management/feature-role-delete/${roleId}/${featureId}`
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public createCustomRole(body: CreateCustomRoleModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.loginApi}/roles/create-custom-role`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getMenuByRole(roleId: string): Observable<any> {
    return this.httpRequestService.get(
      `${this.loginApi}/feature-management/get-menu-by-role/${roleId}`
    );
    // .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }
}
